import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { sortBy, prop, toLower, compose, reverse, pathOr } from 'ramda';
import moment from 'moment';
import PagedTable from '../PagedTable';
import ClickSelectText from '../ClickSelectText';
import userSources from '../../sources/user';
import { addNotification } from '../../actions/reduxActions/notification';
import { fetchCurrentUser } from '../../actions/reduxActions/currentUser';
import { notificationTypes } from '../../constants';
import translations from '../../translations';
import { isNullOrWhitespace, getByID } from '../../utils/core';
import Confirm from '../generic/confirm';
import ShowHide from '../generic/ShowHide';

export const UserSettings = ({ currentUser, addNotificationAction, fetchCurrentUserAction }) => {

    const { authenticationToken, tenants } = currentUser; 
    const APIKeysPageSize = 20;

    // State
    const [APIKeys, setAPIKeys] = useState([]);
    const [APIKeysLoading, setAPIKeysLoading] = useState(true);
    const [APIKeysPage, setAPIKeysPage] = useState(1);
    const [selectedTenantId, setSelectedTenantId] = useState(null);
    const [keyName, setKeyName] = useState('');
    const [selectedKey, setSelectedKey] = useState(null);
    const [showConfirmRemoveKey, setShowConfirmRemoveKey] = useState(false);

    const locale = window.navigator.userLanguage || window.navigator.language;
    moment.locale(locale);
    
    const getTenantName = tenantId => pathOr('', ['developerName'], getByID(tenantId, tenants || []));
    const sortByDeveloperNameCaseInsensitive = sortBy(compose(toLower, prop('developerName')));
    const sortByCreatedAt = compose(reverse, sortBy(prop('createdAt')));

    const fetchAPIKeys = () => userSources.getKeys(authenticationToken).then((keys) => {
        setAPIKeys(sortByCreatedAt(keys));
        setAPIKeysLoading(false);
    })
        .catch((error) => {
            addNotificationAction({
                type: notificationTypes.ERROR,
                message: error.message,
                isPersistent: true,
            });
        });

    const onGenerateKey = () => {

        if (isNullOrWhitespace(keyName) || isNullOrWhitespace(selectedTenantId)) {
            addNotificationAction({
                type: notificationTypes.ERROR,
                message: translations.USER_api_key_validation_message,
            });
            return;
        }

        userSources.postKey({ authenticationToken, keyName, tenantId: selectedTenantId })
            .then(() => {
                addNotificationAction({
                    type: notificationTypes.SUCCESS,
                    message: 'API Key successfully generated',
                });
                setKeyName('');
                setSelectedTenantId('');
                fetchAPIKeys();
            })
            .catch((error) => {
                addNotificationAction({
                    type: notificationTypes.ERROR,
                    message: error.message,
                    isPersistent: true,
                });
            });
    };

    const onRemoveKey = () => {
        userSources.deleteKey({ authenticationToken, keyName: selectedKey.name })
            .then(() => {
                addNotificationAction({
                    type: notificationTypes.SUCCESS,
                    message: 'API Key successfully deleted',
                });
                setShowConfirmRemoveKey(false);
                setSelectedKey(null);
                fetchAPIKeys();
            })
            .catch((error) => {
                addNotificationAction({
                    type: notificationTypes.ERROR,
                    message: error.message,
                    isPersistent: true,
                });
            });
    };

    const APIKeysColumns = [
        {
            renderHeader: () => 'Actions',
            renderCell: ({ item }) => (
                <button 
                    className="btn-icon danger" 
                    title="Delete Key"
                    onClick={() => {
                        setShowConfirmRemoveKey(true); 
                        setSelectedKey(item);
                    }}
                >
                    <span className="glyphicon glyphicon-trash" />
                </button>
            ),
            size: '1px',
        },
        {
            renderHeader: () => 'Key Name',
            renderCell: ({ item }) => item.name,
        },
        {
            renderHeader: () => 'Tenant',
            renderCell: ({ item }) => getTenantName(item.tenantId),
        },
        {
            renderHeader: () => 'Created',
            renderCell: ({ item }) => moment(item.createdAt).format('lll'),
        },
        {
            renderHeader: () => 'Key',
            renderCell: ({ item }) => (
                <ShowHide
                    renderTrigger={isShowing => !isShowing && (
                        <span className="link-emulate">Show</span>
                    )}
                >
                    <ClickSelectText text={item.apiKey} />
                </ShowHide>
            ),
            size: '400px',
        },
    ];

    const tenantsSortedByName = sortByDeveloperNameCaseInsensitive(tenants || []);

    useEffect(() => {  
        // Refresh the tenants in the currentUser app state
        fetchCurrentUserAction(authenticationToken);      
        fetchAPIKeys();
    }, []);

    return (
        <span className="admin-transition">
            <div className="flex-column full-height">
                <div className="admin-page">

                    <h1>User Settings</h1>

                    <h2>
                        API keys
                    </h2>
                    <p>{ translations.USER_api_key_description }</p>
                    
                    <h3>
                        Generate a new API key
                    </h3>
                    <div className="flex margin-bottom" onSubmit={onGenerateKey}>
                        <label className="control-label margin-bottom-none">
                            <span className="margin-bottom-small block">
                                Key Name 
                                <span className="input-required"> *</span>
                            </span>                            
                            <input
                                value={keyName}
                                onChange={({ target: { value } }) => setKeyName(value)}
                                className="form-control" 
                            />
                        </label>
                        <label className="control-label margin-left margin-bottom-none">
                            <span className="margin-bottom-small block">
                                Tenant 
                                <span className="input-required"> *</span>
                            </span>
                            <select
                                data-component="tenant-filter"
                                className="form-control form-select"
                                value={selectedTenantId || ''}
                                onChange={({ target: { value } }) => setSelectedTenantId(value)}
                            >
                                {
                                    <option value="" key="please-select">Please select a tenant</option>
                                }
                                {
                                    tenantsSortedByName.map(tenant => <option value={tenant.id} key={tenant.id}>{tenant.developerName}</option>)
                                }
                            </select>
                        </label> 
                        <button type="button" className="btn btn-primary margin-left flex-end" onClick={onGenerateKey}>Generate Key</button>
                    </div>    
                    <PagedTable 
                        className="table margin-top"
                        columns={APIKeysColumns} 
                        items={APIKeys}
                        page={APIKeysPage}
                        pageSize={APIKeysPageSize}
                        onPageClick={setAPIKeysPage}
                        isLoading={APIKeysLoading}
                        keyGenerator={({ apiKey }) => apiKey}
                    />       
                    <Confirm
                        title={translations.USER_api_key_remove_confirm_title}
                        messages={[
                            translations.USER_api_key_remove_confirm_message,
                        ]} 
                        show={showConfirmRemoveKey}
                        onConfirm={onRemoveKey}
                        onCancel={() => {
                            setShowConfirmRemoveKey(false);
                            setSelectedKey(null);
                        }}
                        buttonStyle="danger"
                        buttonCaption="Delete"
                    />         
                </div>
            </div>
        </span>
    );
};

const mapStateToProps = ({ currentUser }) => ({ currentUser });
const mapDispatchToProps = {
    addNotificationAction: addNotification, 
    fetchCurrentUserAction: fetchCurrentUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserSettings);
